import React, { useState, useRef } from 'react';
import { User, Lock, Eye, EyeOff, Hotel, Wallet } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import Logo from '../components/Logo';
import ParticlesBackground from '../components/ParticlesBackground';

export default function SignInPage({ onLogin }) {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [credentials, setCredentials] = useState({ login: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const handleSubmit = async () => {
    setError('');
    if (!captchaToken) {
      setError("Veuillez confirmer que vous n'êtes pas un robot.");
      return;
    }
    setIsLoading(true);
    try {
      await onLogin({ ...credentials, captchaToken });
    } catch (err) {
      setError(err.message || 'Identifiants invalides');
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ background: '#0d1b2e' }}>
      <ParticlesBackground />

      {/* Background blobs */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full animate-pulse" style={{ background: 'rgba(232,69,69,0.15)', filter: 'blur(80px)' }}></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full animate-pulse" style={{ background: 'rgba(255,123,71,0.15)', filter: 'blur(80px)', animationDelay: '1s' }}></div>
      </div>

      <div className="w-full max-w-6xl grid md:grid-cols-2 relative z-10 rounded-2xl overflow-hidden" style={{ border: '0.5px solid rgba(255,255,255,0.08)' }}>

        {/* Left Side - Login form (white) */}
        <div className="bg-white p-8 md:p-12">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ color: '#0d1b2e' }}>Connexion</h1>
            <p className="text-gray-500">Accédez à votre espace partenaire</p>
          </div>

          <div className="space-y-6">
            {error && (
              <div className="p-4 rounded-lg" style={{ background: 'rgba(232,69,69,0.08)', borderLeft: '4px solid #e84545' }}>
                <p className="text-sm" style={{ color: '#e84545' }}>{error}</p>
              </div>
            )}

            <div>
              <label className="flex items-center gap-2 font-semibold mb-2" style={{ color: '#0d1b2e' }}>
                <User size={18} />
                Identifiant
              </label>
              <input
                type="text"
                placeholder="Votre identifiant"
                value={credentials.login}
                onChange={(e) => setCredentials({ ...credentials, login: e.target.value })}
                onKeyPress={handleKeyPress}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 outline-none transition"
                style={{ focusBorderColor: '#e84545' }}
              />
            </div>

            <div>
              <label className="flex items-center gap-2 font-semibold mb-2" style={{ color: '#0d1b2e' }}>
                <Lock size={18} />
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Votre mot de passe"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  onKeyPress={handleKeyPress}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 outline-none transition pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-gray-600">Se souvenir de moi</span>
              </label>
              <button type="button" className="text-sm hover:underline" style={{ color: '#e84545' }}>
                Mot de passe oublié ?
              </button>
            </div>

            <div className="flex justify-center">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6LfqrdcsAAAAAJm7YqmmtmA_1mEB-SdHjHFrdBI0"
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={isLoading || !captchaToken}
              className="w-full text-white font-semibold py-3 rounded-lg transition duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              style={{ background: isLoading || !captchaToken ? '#ccc' : 'linear-gradient(135deg, #e84545, #FF7B47)' }}
            >
              {isLoading ? 'CONNEXION...' : 'SE CONNECTER'}
            </button>
          </div>

          <div className="mt-6 text-center text-sm text-gray-400">
            © {new Date().getFullYear()} DEVHOS
          </div>
        </div>

        {/* Right Side - Dark navy with red/orange accents */}
        <div className="text-white p-8 md:p-12" style={{ background: 'linear-gradient(135deg, #0d1b2e 0%, #1a2e4a 60%, #0d1b2e 100%)', boxShadow: '-8px 0 40px rgba(255, 255, 255, 0.24), inset 0 0 60px rgba(255, 255, 255, 0.24)' }}>
          {/* <div className="mb-8">
            <Logo />
          </div> */}

          <h2 className="text-4xl font-bold mb-4">Bienvenue !</h2>
          <p className="text-lg mb-8" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Connectez-vous à votre espace B2B et profitez de tarifs exclusifs.
          </p>

          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(232,69,69,0.3)' }}>
              <div className="p-3 rounded-lg" style={{ background: '#e84545' }}>
                <Hotel size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Réservations Instantanées</h3>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>Accès en temps réel</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,123,71,0.3)' }}>
              <div className="p-3 rounded-lg" style={{ background: '#FF7B47' }}>
                <Wallet size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Tarifs B2B Exclusifs</h3>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>Marges garanties</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(232,69,69,0.3)' }}>
              <div className="p-3 rounded-lg" style={{ background: '#e84545' }}>
                <User size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Support 24/7</h3>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>Assistance dédiée</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}